// mailListAttachments.ts — Cluster POSTFACH (G1)
//
// Anhang-Beschaffung für eine Mail der LISTE, wortgleich zur Logik des
// Detailfensters (MailDetailWindow, „Anhänge über ALLE Nachrichten der
// Konversation"). Das ist keine Doppelung aus Bequemlichkeit, sondern die
// Bedingung dafür, dass „Vorgang erstellen" aus dem Kontextmenü dieselben
// Anhänge anbietet wie aus der geöffneten Mail.
//
// Der springende Punkt ist `attachmentMessageIds`: der Listen-Repräsentant einer
// gruppierten Konversation trägt den Anhang oft gar nicht — er hängt an einer
// FRÜHEREN Nachricht des Threads. Wer nur `email.id` abfragt, bekommt genau die
// Anhänge nicht, die der Nutzer übernehmen will.
//
// Den Self-Heal-Rekey des Detailfensters (`resolvedId`) gibt es hier bewusst
// nicht: in der Liste IST `email.id` die aktuelle Id; geheilt wird erst, wenn
// eine Mail geöffnet und dabei eine tote Id bemerkt wird.

import {
    dedupeAttachments,
    graphMailService,
    type Email,
    type MailAttachment,
} from "../../services/graphMailService";
import { reportMailError } from "../../services/mailError";
import { bilderFuerVorgang } from "./inlineBilder";

export type MailListAttachments = {
    items: MailAttachment[];
    /**
     * T-236: Bilder aus dem Mailtext, die für einen Vorgang in Frage kommen
     * (ohne Signatur-Logos). Getrennt von `items`, damit kein anderer Weg sie
     * versehentlich als Datei-Anhang mitnimmt.
     */
    bilder: MailAttachment[];
    /** true = mindestens ein Teilabruf ist gescheitert; `items` ist dann leer. */
    failed: boolean;
};

/**
 * Datei-Anhänge einer Listen-Mail über die GANZE Konversation, dazu die Bilder
 * aus dem Mailtext.
 *
 * Wirft nicht: ein Fehlschlag darf den Erstell-Flow nicht verhindern — er wird
 * über `failed` gemeldet, und der Vorgang entsteht dann eben ohne Anhänge.
 */
export async function buildMailAttachments(
    mailbox: string,
    email: Email,
): Promise<MailListAttachments> {
    const dateiIds = email.attachmentMessageIds?.length
        ? email.attachmentMessageIds
        : email.hasAttachments
          ? [email.id]
          : [];
    // Graph meldet `hasAttachments: false`, wenn eine Mail NUR eingebettete
    // Bilder trägt — die angeklickte Nachricht wird darum immer mit abgefragt.
    // Scheitert nur diese Zusatzabfrage, bleibt es still bei „keine Bilder".
    const ids = dateiIds.includes(email.id) ? dateiIds : [...dateiIds, email.id];

    const ergebnisse = await Promise.allSettled(
        ids.map((mid) => graphMailService.fetchAttachments(mailbox, mid)),
    );
    const fehler = ergebnisse.find(
        (r, i): r is PromiseRejectedResult => r.status === "rejected" && dateiIds.includes(ids[i]),
    );
    if (fehler) {
        reportMailError({
            area: "mail",
            action: "load-attachments",
            error: fehler.reason,
            fallbackMessage: "Die Mail-Anhänge konnten nicht geladen werden.",
        });
        return { items: [], bilder: [], failed: true };
    }
    const aus = (nurDateiIds: boolean) =>
        dedupeAttachments(
            ergebnisse.flatMap((r, i) =>
                r.status === "fulfilled" && (!nurDateiIds || dateiIds.includes(ids[i])) ? r.value : [],
            ),
        );
    return {
        // Inline-Teile raus (Signatur-Logos): sie stehen im Body und hätten in
        // einer Datei-Liste nichts verloren — dieselbe Filterung wie im
        // Detailfenster. Nur Nachrichten mit Datei-Anhängen zählen hier.
        items: aus(true).filter((a) => !a.isInline),
        bilder: bilderFuerVorgang(aus(false)),
        failed: false,
    };
}
